import React from "react";
import { Box, Typography, styled } from "@mui/material";
import { Chip } from "../Chip";
import { RangeChip } from "../RangeChip";
import { Icon } from "../Icon";
import { OverflowTooltip } from "../OverflowTooltip";
import { IssueBoxItemProps } from "./types";

const StyledLeftSlot = styled(Box)({
  width: "16px",
  minWidth: "16px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "stretch",
  position: "relative",
});

const StyledContentContainer = styled(Box)({
  width: "100%",
  // 액션 슬롯이 붙어 행이 넘칠 때 줄어드는 쪽은 이름(ellipsis)이어야 한다 — flex 기본 min-width:auto 를 풀어 준다
  minWidth: 0,
  height: "100%",
  display: "flex",
  alignItems: "center",
  padding: "0 10px",
  gap: "16px",
});

const StyledTextContainer = styled(Box)({
  maxWidth: "120px",
  display: "flex",
  flexDirection: "column",
  gap: "2px",
});

const StyledRightSlot = styled(Box)({
  height: "46px",
  // 타입 칩·날짜는 잘리지 않는다 — 넘침은 본문(이름) 쪽이 흡수한다
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  justifyContent: "flex-start",
  marginTop: "7px",
});

// 우측 슬롯(타입 칩·날짜)과 액션 슬롯 사이 간격 — 본문 gap(16px)보다 좁혀 한 묶음으로 읽히게 한다
const ACTION_SLOT_GAP = "8px";

// 타입 매핑
const typeIconMap = {
  marker: "12/tool/pin",
  line: "12/tool/line",
  polygon: "12/tool/shape",
  text: "12/tool/text",
  rectangle: "12/tool/square",
} as const;

// 등급별 색상 매핑
const gradeColorMap = {
  1: "#4CAF50", // 낮음 (녹색)
  2: "#FF9800", // 보통 (주황)
  3: "#F44336", // 높음 (빨강)
  4: "#9C27B0", // 매우 높음 (보라)
  5: "#000000", // 치명적 (검정)
} as const;

export const IssueBoxItem: React.FC<IssueBoxItemProps> = ({
  issueId,
  name,
  category,
  grade,
  severityColor,
  type,
  updatedAt,
  onClick,
  actionSlot,
  className,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) {
      onClick(issueId);
    }
  };

  // grade 값이 1-5 범위를 벗어나면 기본 색상 사용
  const validGrade = grade && grade >= 1 && grade <= 5 ? grade : 1;
  const finalSeverityColor =
    severityColor ||
    gradeColorMap[validGrade as keyof typeof gradeColorMap] ||
    "#F44336";
  const iconName = typeIconMap[type] || "12/tool/pin";

  return (
    <Box
      onClick={handleClick}
      className={className}
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: "52px",
        paddingLeft: "16px",
        paddingRight: "16px",
        backgroundColor: "transparent",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
        "&:active": {
          backgroundColor: "rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      <StyledLeftSlot>
        <Box
          component="img"
          src="/icons/etc/divider.svg"
          alt="divider"
          sx={{
            width: "auto",
            height: "100%",
            objectFit: "contain",
          }}
        />
      </StyledLeftSlot>

      <StyledContentContainer>
        <StyledTextContainer>
          <OverflowTooltip
            text={name}
            variant="body2"
            sx={{
              fontFamily: "Pretendard",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "16.8px",
              letterSpacing: "0.17px",
              color: "rgba(0, 0, 0, 0.87)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          />
          <RangeChip
            text={true}
            size="s"
            colorCode={finalSeverityColor}
            label={category}
            activeDots={validGrade}
          />
        </StyledTextContainer>
      </StyledContentContainer>

      <StyledRightSlot>
        <Chip
          appearance="default"
          leftIcon={<Icon name={iconName} size={12} />}
          label={type}
          showRightIcon={false}
        />
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: "12px",
            color: "rgba(0, 0, 0, 0.38)",
            textAlign: "right",
            letterSpacing: "0.4px",
            lineHeight: 1.66,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {updatedAt}
        </Typography>
      </StyledRightSlot>

      {actionSlot && (
        <Box
          // 슬롯 안의 버튼 클릭이 행 선택(onClick)으로 번지지 않게 여기서 끊는다 — Container 의 rightSlot 과 같은 격리
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            marginLeft: ACTION_SLOT_GAP,
          }}
        >
          {actionSlot}
        </Box>
      )}
    </Box>
  );
};
